<script setup lang="ts">
import VsText from '@/components/vision/VsText.vue'
import { computed, onMounted, ref, watch, nextTick, PropType } from 'vue'
import type { MultiselectOption, JobFilterComparison, JobLookupFilterValue } from '@ironsight/types'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import {
  optionMatchesMultiselectSearchText,
  useMultiselectSelectAll,
} from '@/components/jobs/job-search-page/useMultiselectSelectAll'

const props = defineProps({
  value: {
    type: Array as PropType<JobLookupFilterValue>,
    default: () => [],
  },
  options: {
    type: Array as PropType<MultiselectOption[]>,
    default: () => [],
  },
  comparison: {
    type: String as PropType<JobFilterComparison>,
    required: true,
  },
  comparisonOptions: {
    type: Array as PropType<MultiselectOption[]>,
    default: () => [],
  },
  label: {
    type: String,
    default: '',
  },
  allowCreateFromSearch: {
    type: Boolean,
    default: false,
  },
  supportsSelectAll: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'input', value: MultiselectOption[]): void
  (e: 'operation-change', value: JobFilterComparison): void
}>()

const DEFAULT_SCROLLER_HEIGHT = 300
const searchText = ref<string>('')
const searchInputRef = ref<InstanceType<typeof VsTextInput> | null>(null)
const containerEl = ref<HTMLDivElement | null>(null)
const scrollerHeight = ref(DEFAULT_SCROLLER_HEIGHT)

const checkedBoxes = ref<Array<string | number>>([])

function getOptionForValue(value: string | number): MultiselectOption {
  const option = props.options.find((opt) => opt.value === value)
  if (option) {
    return option
  }

  const selectedOption = props.value.find(
    (item) => typeof item === 'object' && item !== null && 'value' in item && item.value === value,
  ) as MultiselectOption | undefined

  return selectedOption || { label: String(value), value }
}

const filteredOptions = computed<MultiselectOption[]>(() => {
  const normalizedSearchText = searchText.value.trim()

  if (!normalizedSearchText) {
    return props.options
  }

  const formattedSearchText = normalizedSearchText.toLowerCase()
  return props.options.filter((option) =>
    optionMatchesMultiselectSearchText(option, formattedSearchText),
  )
})

const {
  isSelectAllMode,
  optionMatchesSearchText,
  isItemChecked,
  includeItem,
  toggleItem,
  selectAllFiltered: selectAllFilteredValue,
  deselectAllFiltered: deselectAllFilteredValue,
  areAllFilteredSelected,
  getOptionsFromCheckedBoxes,
  mapCheckedBoxValues,
  checkedBoxValuesChanged,
} = useMultiselectSelectAll({
  value: () => props.value,
  searchText,
  checkedBoxes,
  filteredOptions: () => filteredOptions.value,
  resolveOption: getOptionForValue,
  emitInput: (value) => emit('input', value),
  searchSelectAllMode: 'visible-options',
})

type OptionItem = MultiselectOption & { isFirstWithDivider?: boolean }
type CreateOptionItem = {
  label: string
  value: string
  rawValue: string
  isCreateOption: true
  isFirstWithDivider?: boolean
}
type DisplayOptionItem = OptionItem | CreateOptionItem

const displayedOptions = ref<DisplayOptionItem[]>([])

const itemHeight = computed(() => {
  const hasDescriptions = displayedOptions.value.some(
    (item) => 'description' in item && item.description,
  )
  return hasDescriptions ? 52 : 40
})

const normalizedSearchText = computed(() => searchText.value.trim())

const selectedItems = computed<OptionItem[]>(() => {
  return getOptionsFromCheckedBoxes().map((option) => ({
    ...option,
    label: option.label ?? String(option.value),
  }))
})

const createOption = computed<CreateOptionItem | null>(() => {
  if (!props.allowCreateFromSearch || !normalizedSearchText.value) {
    return null
  }

  const normalizedValue = normalizedSearchText.value.toLowerCase()
  const exactMatchExists = props.options.some(
    (option) => option.label.trim().toLowerCase() === normalizedValue,
  )
  const alreadySelected = checkedBoxes.value.some(
    (value) => String(value).trim().toLowerCase() === normalizedValue,
  )

  if (exactMatchExists || alreadySelected) {
    return null
  }

  return {
    label: `Use "${normalizedSearchText.value}"`,
    value: normalizedSearchText.value,
    rawValue: normalizedSearchText.value,
    isCreateOption: true,
  }
})

function updateDisplayedOptions() {
  const createOptionItem = createOption.value
  const remainingOptions = filteredOptions.value.map((option) => ({ ...option }))

  if (isSelectAllMode.value) {
    displayedOptions.value = createOptionItem
      ? [...remainingOptions, createOptionItem]
      : remainingOptions
    return
  }

  const selectedSet = new Set(checkedBoxes.value)
  const search = normalizedSearchText.value.toLowerCase()

  const visibleSelectedItems: OptionItem[] = search
    ? selectedItems.value.filter((item) => optionMatchesSearchText(item, search))
    : selectedItems.value

  const unselectedItems: OptionItem[] = remainingOptions.filter(
    (option) => !selectedSet.has(option.value),
  )
  const remainingItems = createOptionItem ? [...unselectedItems, createOptionItem] : unselectedItems

  const withDivider =
    visibleSelectedItems.length > 0 && remainingItems.length > 0
      ? ([
          { ...remainingItems[0], isFirstWithDivider: true },
          ...remainingItems.slice(1),
        ] as typeof remainingItems)
      : remainingItems

  displayedOptions.value = [...visibleSelectedItems, ...withDivider]
}

function keepCreatedOptionInPlace(item: CreateOptionItem) {
  displayedOptions.value = displayedOptions.value.map((displayedItem) => {
    if ('isCreateOption' in displayedItem && displayedItem.isCreateOption) {
      return {
        label: item.rawValue,
        value: item.rawValue,
        isFirstWithDivider: displayedItem.isFirstWithDivider,
      }
    }

    return displayedItem
  })
}

function updateSearchText(text: string) {
  searchText.value = text
}

function emitSelectedOptions() {
  emit('input', getOptionsFromCheckedBoxes())
}

function handleItemClick(item: DisplayOptionItem) {
  if ('isCreateOption' in item && item.isCreateOption) {
    if (isSelectAllMode.value) {
      includeItem({ label: item.rawValue, value: item.rawValue })
      return
    }

    checkedBoxes.value.push(item.rawValue)
    keepCreatedOptionInPlace(item)
    emitSelectedOptions()
    return
  }

  toggleItem(item)
}

watch(
  () => props.value,
  (newValue) => {
    const nextValues = mapCheckedBoxValues(newValue)
    const hasChanged = checkedBoxValuesChanged(nextValues)
    checkedBoxes.value = nextValues

    if (hasChanged) {
      updateDisplayedOptions()
    }
  },
  { immediate: true },
)

watch(
  [filteredOptions, normalizedSearchText],
  () => {
    updateDisplayedOptions()
  },
  { immediate: true },
)

onMounted(() => {
  searchInputRef.value?.focus()
  nextTick(() => {
    if (containerEl.value) {
      const rect = containerEl.value.getBoundingClientRect()
      const height = window.innerHeight - rect.top
      scrollerHeight.value = Math.max(DEFAULT_SCROLLER_HEIGHT, height)
    }
  })
})

function selectAllFiltered() {
  selectAllFilteredValue()
  updateDisplayedOptions()
}

function deselectAllFiltered() {
  deselectAllFilteredValue()
  updateDisplayedOptions()
}

defineExpose(
  props.supportsSelectAll
    ? {
        supportsSelectAll: true,
        filteredOptions,
        selectAllFiltered,
        deselectAllFiltered,
        areAllFilteredSelected,
      }
    : {},
)
</script>

<template>
  <div ref="containerEl" class="filter-options-container">
    <div class="filter-search">
      <vs-text-input
        ref="searchInputRef"
        :value="searchText"
        :label="props.label || 'Search'"
        @input="updateSearchText"
      >
        <template #append>
          <v-icon>search</v-icon>
        </template>
      </vs-text-input>
    </div>

    <div v-if="comparisonOptions.length > 1" class="operation-selector d-flex">
      <v-select
        :value="comparison"
        :items="comparisonOptions"
        :menu-props="{
          rounded: 'xl',
          nudgeBottom: 34,
          contentClass: 'operation-menu',
        }"
        rounded
        item-text="label"
        item-value="value"
        hide-details
        @input="emit('operation-change', $event)"
      />
    </div>

    <div v-if="!displayedOptions.length" class="empty-results">
      <vs-text center type="subdued">No results</vs-text>
    </div>

    <v-virtual-scroll
      :items="displayedOptions"
      class="virtual-scroll-container"
      :item-height="itemHeight"
      :height="scrollerHeight"
    >
      <template #default="{ item }">
        <v-list-item
          v-if="'isCreateOption' in item && item.isCreateOption"
          :key="`create-${item.rawValue}`"
          class="list-item create-item"
          :class="{ 'item-with-divider': item.isFirstWithDivider }"
          @click="handleItemClick(item)"
        >
          <v-list-item-content>
            <vs-text type="paragraph">
              {{ item.label }}
            </vs-text>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-else
          :key="item.value"
          dense
          class="list-item"
          :class="{ 'item-with-divider': item.isFirstWithDivider }"
          :two-line="!!item.description"
          @click="handleItemClick(item)"
        >
          <v-list-item-action>
            <v-checkbox
              :input-value="isItemChecked(item)"
              color="var(--color-blue)"
              hide-details
              readonly
            />
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title :title="item.label">
              <vs-text type="paragraph" :title="item.label" truncate>
                {{ item.label }}
              </vs-text>
            </v-list-item-title>
            <v-list-item-subtitle v-if="item.description" :title="item.description">
              <vs-text type="caption" :title="item.description" truncate>
                {{ item.description }}
              </vs-text>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-virtual-scroll>
  </div>
</template>

<style scoped>
.filter-options-container {
  background: white;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filter-search {
  border-radius: 8px !important;
  max-width: 100%;
  margin-bottom: 8px;
}

.operation-selector {
  background-color: var(--color-white);
  border: 1px solid var(--color-grey--lighter);
  border-radius: 32px !important;
  max-width: 100%;
  height: 46px;
}

.operation-selector :deep(.v-input) {
  height: 46px;
}

.operation-selector :deep(.v-select__selection) {
  margin-top: -26px !important;
  color: var(--color-grey) !important;
}

.operation-selector :deep(.v-input__slot) {
  min-height: 46px !important;
  padding-left: 16px !important;
}

.operation-selector :deep(.v-select__slot) {
  height: 46px;
  display: flex;
}

.operation-selector :deep(.v-input__append-inner) {
  margin-top: -32px !important;
  margin-right: -8px !important;
  align-self: center;
}

.operation-selector :deep(.v-input__append-inner .v-icon) {
  color: var(--color-grey--light) !important;
}

.virtual-scroll-container {
  overflow: auto;
  white-space: nowrap;
  background: white;
  flex: 1;
}

.empty-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: var(--color-grey);
}

.list-item {
  transition: background-color 0.15s ease;
  padding: 0 12px !important;
  margin-bottom: 2px;
}

.list-item.item-with-divider {
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid var(--color-grey--lighter);
}

.list-item :deep(.v-list-item__action) {
  margin-right: 16px;
}

.list-item.v-list-item--two-line {
  min-height: 52px;
}

.list-item.v-list-item--two-line :deep(.v-list-item__content) {
  padding: 4px 0;
}

.list-item :deep(.v-list-item__subtitle) {
  margin-top: -4px;
}

.create-item {
  min-height: 40px;
}
</style>
